<script setup>
/* eslint-disable vue/custom-event-name-casing */
/* global axios */
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { moneyFor } from 'dashboard/helper/currencyFormat';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  emitOnClick: {
    type: Boolean,
    default: false,
  },
  highlightDealId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['deal-click', 'loaded']);

const route = useRoute();
const router = useRouter();

const accountId = computed(() => route.params.accountId);

const deals = ref([]);
const isLoading = ref(false);
const hasError = ref(false);

const isEmpty = computed(() => !isLoading.value && deals.value.length === 0);

const statusConfig = {
  open: {
    label: 'Aberto',
    class: 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400',
  },
  won: {
    label: 'Ganho',
    class:
      'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400',
  },
  lost: {
    label: 'Perdido',
    class: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400',
  },
};

const fetchDeals = async () => {
  if (!props.contactId || !accountId.value) return;

  isLoading.value = true;
  hasError.value = false;

  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/deals`
    );
    deals.value = response.data?.payload || [];
    emit('loaded', deals.value.length);
  } catch {
    hasError.value = true;
    deals.value = [];
    emit('loaded', 0);
  } finally {
    isLoading.value = false;
  }
};

// Moeda do proprio registro quando existir; senao a da conta.
const formatCurrency = (value, code) => {
  if (!value && value !== 0) return '-';
  return moneyFor(value, code);
};

const formatDate = dateStr => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const navigateToDeal = deal => {
  // deal em modo RESUMO (deal_view_basic): a tela do deal e proibida pela
  // policy — clicar so daria 403, entao o card nem navega
  if (deal.basic) return;
  if (props.emitOnClick) {
    emit('deal-click', deal);
    return;
  }
  router.push({
    name: 'crm_item_detail',
    params: { accountId: accountId.value, itemId: deal.id },
  });
};

watch(
  () => props.contactId,
  newId => {
    if (newId) {
      deals.value = [];
      fetchDeals();
    }
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, vue/custom-event-name-casing -->
  <div :class="compact ? 'px-4 py-2' : 'px-6 py-4'">
    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Error -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center py-8 gap-2"
    >
      <span class="i-lucide-alert-circle size-6 text-n-slate-10" />
      <p class="text-sm text-n-slate-10 mb-0">Erro ao carregar negocios.</p>
      <button
        class="text-xs text-woot-500 hover:text-woot-600 font-medium border-0 bg-transparent cursor-pointer"
        @click="fetchDeals"
      >
        Tentar novamente
      </button>
    </div>

    <!-- Empty -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-8 gap-1.5"
    >
      <span class="i-lucide-briefcase size-6 text-n-slate-9" />
      <p class="text-sm text-n-slate-10 mb-0">
        Nenhum negocio associado a este contato.
      </p>
    </div>

    <!-- Deal list -->
    <transition-group
      v-else
      name="deal-fade"
      tag="div"
      class="flex flex-col gap-2"
    >
      <button
        v-for="deal in deals"
        :key="deal.id"
        class="deal-item flex flex-col gap-1.5 p-3 rounded-lg border border-n-weak bg-n-alpha-black2 text-left w-full"
        :class="{
          'deal-highlight': deal.id === highlightDealId,
          'hover:bg-n-alpha-2 cursor-pointer': !deal.basic,
          'cursor-default opacity-90': deal.basic,
        }"
        @click="navigateToDeal(deal)"
      >
        <div class="flex items-center justify-between w-full">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ deal.title }}
          </span>
          <span
            v-if="deal.basic"
            class="inline-flex flex-shrink-0 items-center gap-1 rounded bg-n-slate-4 px-1.5 py-px text-[9.5px] font-semibold text-n-slate-11"
            :title="$t('CONTACTS_SIDEBAR.DEALS.BASIC_TOOLTIP')"
          >
            <span class="i-lucide-lock size-2.5" />
            {{ $t('CONTACTS_SIDEBAR.DEALS.BASIC_BADGE') }}
          </span>
          <span
            v-else-if="deal.status"
            class="text-[10px] font-semibold px-1.5 py-0.5 rounded-md flex-shrink-0"
            :class="statusConfig[deal.status]?.class || statusConfig.open.class"
          >
            {{ statusConfig[deal.status]?.label || deal.status }}
          </span>
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-10">
          <span v-if="deal.pipeline_name" class="truncate">
            {{ deal.pipeline_name }}
          </span>
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <span
            v-if="deal.pipeline_active === false"
            class="inline-flex flex-shrink-0 items-center gap-1 rounded bg-n-slate-4 px-1.5 py-px text-[9.5px] font-semibold text-n-slate-11"
          >
            <span class="i-lucide-archive size-2.5" />
            arquivado
          </span>
          <span v-if="deal.pipeline_name && deal.stage_name">-</span>
          <span v-if="deal.stage_name" class="truncate">
            {{ deal.stage_name }}
          </span>
        </div>
        <div class="flex items-center justify-between w-full text-xs">
          <span v-if="deal.basic" class="text-n-slate-10 truncate">
            {{
              deal.owner_name
                ? $t('CONTACTS_SIDEBAR.DEALS.BASIC_OWNER', {
                    name: deal.owner_name,
                  })
                : $t('CONTACTS_SIDEBAR.DEALS.BASIC_NO_OWNER')
            }}
          </span>
          <span v-else class="font-medium text-n-slate-11">
            {{ formatCurrency(deal.total_value, deal.currency) }}
          </span>
          <span class="text-n-slate-9">
            {{ formatDate(deal.created_at) }}
          </span>
        </div>
      </button>
    </transition-group>
  </div>
</template>

<style scoped>
.deal-item {
  transition:
    background-color 1.5s ease-out,
    border-color 1.5s ease-out;
}

.deal-fade-enter-active {
  transition: all 0.4s ease-out;
}
.deal-fade-enter-from {
  opacity: 0;
  transform: translateY(-12px);
}

.deal-highlight {
  background-color: rgba(34, 197, 94, 0.18);
  border-color: rgba(34, 197, 94, 0.5);
  animation: pulse-highlight 0.7s ease-out;
}

@keyframes pulse-highlight {
  0% {
    background-color: rgba(34, 197, 94, 0.4);
    border-color: rgba(34, 197, 94, 0.9);
  }
  100% {
    background-color: rgba(34, 197, 94, 0.18);
    border-color: rgba(34, 197, 94, 0.5);
  }
}
</style>
